import { CppOrganization } from "@civicplus/preamble-ui/lib/Services/OrgService/Types/Organization";
import { User } from "oidc-client-ts";
import { UserJobInformation } from "../../entities/User";
import { UserService } from "../../services/UserService";
import { bottle } from "../../provider/Bottle";
import { useEffect, useState } from "react";
import { getCurrentOrigin } from "../../util/windowHelper";

const ignoredOrgsPendo = ["ui-automation", "kraken", "cp-internal"];

export interface VisitorMetadata {
    id: string;
    notifications_role: string;
    cp_employee: boolean;
    notifications_customer: boolean;
    email?: string | undefined;
    name?: string;
    department?: string | undefined;
    focusArea?: string | undefined;
    notifications_environment?: string;
}
export const getEnvironmentFromUrl = (origin: string): string => {
    let env = "";
    switch (true) {
        case origin.includes("-pr"):
            env = "pr";
            break;
        case origin.includes("-qa"):
            env = "qa";
            break;
        case origin.includes("-staging"):
            env = "staging";
            break;
        case origin.includes("-sandbox"):
            env = "sandbox";
            break;
        case origin.includes("localhost"):
            env = "dev";
            break;
        default:
            env = "prod";
    }
    return env;
};

export function usePendo(organization: CppOrganization | undefined, user: User | undefined): void {
    const userService = bottle.container.UserService as UserService;
    const [currentInitializationInProgress, setCurrentInitializationInProgress] = useState("");

    useEffect(() => {
        const sendUserInfoToPendo = async () => {
            let user_role = "Customer Admin";

            let cp_employee = false;
            let notifications_customer = false;

            const is_cp_staff = user?.profile.email?.includes("@civicplus.com");

            if (is_cp_staff) {
                user_role = "CivicPlus Staff";
                cp_employee = true;
            } else {
                user_role = "Customer Admin";
                notifications_customer = true;
            }

            let visitorData: VisitorMetadata = {
                id: user?.profile?.sub ?? "",
                notifications_role: user_role,
                cp_employee,
                notifications_customer
            };

            const accountData = {
                id: organization?.id ?? "",
                name: organization?.name,
                org_friendly_name: organization?.friendlyName ?? "",
                is_notifications: true
            };

            const jobInfo: UserJobInformation = await userService.getJobInfo();

            if (user_role === "CivicPlus Staff" || user_role === "Customer Admin") {
                visitorData = {
                    ...visitorData,
                    email: user?.profile?.email,
                    name: `${user?.profile?.given_name} ${user?.profile?.family_name}`,
                    department: jobInfo?.departmentName,
                    focusArea: jobInfo?.focusAreaName,
                    notifications_environment: getEnvironmentFromUrl(getCurrentOrigin())
                };
            }

            if (window.pendo) {
                window.pendo.initialize({
                    visitor: visitorData,
                    account: accountData
                });
            }
        };

        async function initializePendo() {
            const initializationKey = `${organization?.name}-${user?.profile.sub}`;
            if (currentInitializationInProgress === initializationKey) {
                return;
            }

            if (
                organization &&
                organization.name &&
                !ignoredOrgsPendo.includes(organization.name.toLowerCase()) &&
                user
            ) {
                setCurrentInitializationInProgress(initializationKey);
                await sendUserInfoToPendo();
            }
        }

        if (bottle.container?.FeatureFlags?.enablePendo) {
            initializePendo();
        }
    }, [organization, user, userService]);
}
